/**
 * Bootstrap + main loop. Owns the fixed-timestep accumulator: input is
 * sampled once per sim tick, rendering interpolates between the last two sim
 * states, and audio is slaved to sim time (docs/ARCHITECTURE.md §2, §7).
 */
import { TICK_DT, MAX_TICKS_PER_FRAME } from './engine/core/tick';
import { Canvas2dRenderer } from './render/canvas2d';
import { PostFx } from './render/postfx';
import type { RenderSnapshot } from './render/types';
import { InputSystem } from './input/input';
import { AudioEngine } from './audio/engine';
import type { Track } from './audio/track';
import { NEON_CIRCUIT } from './content/music/neon-circuit';
import { CIRCUIT_BREAKER } from './content/music/circuit-breaker';
import { UNDERCURRENT } from './content/music/undercurrent';
import { FIRST_STEPS } from './content/levels/first-steps';
import { NEON_PRIMER } from './content/levels/neon-primer';
import { AFTERGLOW } from './content/levels/afterglow';
import { NIGHTSHIFT } from './content/levels/nightshift';
import { SIGNAL_STORM } from './content/levels/signal-storm';
import { TERMINAL_VELOCITY } from './content/levels/terminal-velocity';
import { SKY_FOUNDRY } from './content/levels/sky-foundry';
import { GEARWORKS } from './content/levels/gearworks';
import { PARADOX } from './content/levels/paradox';
import { FORM_FOUNDRY } from './content/levels/form-foundry';
import { TWIN_CIRCUITS } from './content/levels/twin-circuits';
import { SWITCHBACK_VAULT } from './content/levels/switchback-vault';
import { PULSE_WORKS } from './content/levels/pulse-works';
import { OVERDRIVE } from './content/levels/overdrive';
import { UNDER_GRID } from './content/levels/under-grid';
import { VOLTAGE } from './content/music/voltage';
import type { LevelData } from './format/types';
import { validateLevel } from './format/validate';
import { t } from './ui/strings';
import { restoreFromMirror } from './game/save-mirror';
import { OnlineClient } from './online/client';
import { OnlineUi } from './ui/online-ui';
import { AdminUi } from './ui/admin-ui';
import { mirrorAll } from './game/save-mirror';
import { parseLevel } from './format/serialize';
import type { Replay } from './engine/replay/replay';

/** Built-in soundtrack registry. Unknown song ids play silent with a warning
 * (missing-audio grace, FEATURE_MATRIX §10). */
const TRACKS: Record<string, Track> = {
  'neon-circuit': NEON_CIRCUIT,
  'circuit-breaker': CIRCUIT_BREAKER,
  undercurrent: UNDERCURRENT,
  voltage: VOLTAGE,
};

const LEVELS: LevelData[] = [
  FIRST_STEPS,
  NEON_PRIMER,
  FORM_FOUNDRY,
  GEARWORKS,
  TWIN_CIRCUITS,
  PULSE_WORKS,
  AFTERGLOW,
  PARADOX,
  OVERDRIVE,
  NIGHTSHIFT,
  SIGNAL_STORM,
  TERMINAL_VELOCITY,
  SWITCHBACK_VAULT,
  UNDER_GRID,
  SKY_FOUNDRY,
];
import { Session, RESPAWN_DELAY_S, type SessionPhase } from './game/session';
import { loadSettings, saveSettings, type Settings } from './game/settings';
import { getProgress } from './game/save';
import {
  loadProfile,
  saveProfile,
  awardCompletion,
  recordDeath,
  purchaseCosmetic,
  claimCache,
  claimContract,
  claimCircuit,
  claimVaultTier,
  ensureContracts,
  COSMETICS,
  type Profile,
} from './game/progression';
import { Ui } from './ui/ui';
import { Editor } from './editor/editor';
import './editor/editor.css';

type AppState = 'menu' | 'playing' | 'paused' | 'editor';

class App {
  private canvas = document.getElementById('game') as HTMLCanvasElement;
  private uiRoot = document.getElementById('ui') as HTMLElement;
  private renderer = new Canvas2dRenderer(this.canvas);
  private postfx = new PostFx(this.canvas);
  private input = new InputSystem();
  private audio = new AudioEngine();
  private settings: Settings = loadSettings();
  private profile: Profile = loadProfile();
  private ui: Ui;
  private state: AppState = 'menu';
  private session: Session | null = null;
  private editor: Editor | null = null;
  private editorHost = document.getElementById('editor-host') as HTMLElement;
  /** True while the current session is an editor playtest. */
  private playtesting = false;
  /** Online services (all optional: the game is fully playable offline). */
  private readonly online = new OnlineClient();
  private onlineUi: OnlineUi | null = null;
  private adminUi: AdminUi | null = null;
  /** Set while playing a downloaded community level (for score submission). */
  private activeOnlineLevelId: number | null = null;
  /** Explicit servers-unreachable escape hatch chosen at the login gate. */
  private offlineMode = false;
  /** Last completed full run (fuels publish + score submission). */
  private lastCompletion: { levelText: string; replay: Replay } | null = null;
  private accumulator = 0;
  private lastFrameMs = 0;
  private prevSnap = { x: 0, y: 0, rot: 0, dualY: 0, dualRot: 0 };
  private currSnap = { x: 0, y: 0, rot: 0, dualY: 0, dualRot: 0 };

  constructor() {
    // Built-in content goes through the same validation as user levels —
    // catches authoring mistakes at startup instead of mid-game.
    for (const level of LEVELS) validateLevel(level as unknown);

    this.ui = new Ui(this.uiRoot, this.settings, {
      onPlay: (i) => {
        if (this.playtesting) {
          this.restartPlaytest();
          return;
        }
        if (!this.canPlay) {
          this.ui.showLoginGate(!this.online.online);
          return;
        }
        this.startLevel(i);
      },
      onOpenEditor: () => {
        if (!this.canPlay) {
          this.ui.showLoginGate(!this.online.online);
          return;
        }
        this.openEditor();
      },
      onGateLogin: (username, password) => this.gateLogin(username, password),
      onGateRegister: (username, password, question, answer) =>
        this.gateRegister(username, password, question, answer),
      onGateQuestion: (username) => this.gateQuestion(username),
      onGateVerify: (username, answer) => this.gateVerify(username, answer),
      onGateReset: (username, answer, password) => this.gateReset(username, answer, password),
      onGateOffline: () => {
        this.offlineMode = true;
        this.ui.hideLoginGate();
        this.showMenu();
      },
      onLogout: () => void this.logout(),
      onEquip: (slot, id) => {
        this.profile.equipped[slot] = id;
        saveProfile(this.profile);
      },
      onClaimVault: (id) => claimVaultTier(this.profile, id)?.label ?? null,
      onOpenOnline: () => this.openOnline(),
      onOpenAdmin: () => this.openAdmin(),
      onPublish: () => void this.publishLastCompletion(),
      getProfile: () => {
        ensureContracts(this.profile);
        return this.profile;
      },
      onBuy: (id) => purchaseCosmetic(this.profile, id),
      onClaimCache: () => claimCache(this.profile),
      onClaimContract: (id) => claimContract(this.profile, id),
      onClaimCircuit: (id) => claimCircuit(this.profile, id),
      onExportSave: () => this.exportSave(),
      onImportSave: () => this.importSave(),
      onPause: () => this.pause(),
      onResume: () => this.resume(),
      onRestart: () => this.restart(),
      onExitToMenu: () => this.exitToMenu(),
      onTogglePractice: () => this.togglePractice(),
      onSettingsChanged: (s) => this.applySettings(s),
      onMetronomeStart: (bpm, beats) => {
        this.audio.unlock();
        return this.audio.startMetronome(bpm, beats);
      },
      onAudioNow: () => this.audio.now(),
      onTouchMove: (dir, down) => {
        if (dir === 'left') this.input.touchLeft = down;
        else this.input.touchRight = down;
      },
    });

    this.input.attach(this.canvas);
    this.input.onPause = () => {
      if (this.playtesting && (this.state === 'playing' || this.state === 'paused')) {
        this.returnToEditor();
      } else if (this.state === 'playing') {
        this.pause();
      } else {
        this.resume();
      }
    };
    this.input.onRestart = () => {
      if (this.state === 'playing') this.restart();
    };
    this.input.onPlaceCheckpoint = () => {
      if (this.state === 'playing' && this.session?.placeCheckpoint()) this.audio.sfxClick();
    };
    this.input.onRemoveCheckpoint = () => {
      if (this.state === 'playing' && this.session?.removeCheckpoint()) this.audio.sfxClick();
    };

    window.addEventListener('resize', () => this.renderer.resize());
    // iOS Safari resizes the visual viewport (URL bar collapse) without
    // always firing window.resize — track it explicitly.
    window.visualViewport?.addEventListener('resize', () => this.renderer.resize());
    document.addEventListener('visibilitychange', () => {
      if (document.hidden && this.state === 'playing') this.pause();
    });
    // Browsers require a user gesture before audio; unlock on the first one.
    const unlock = (): void => this.audio.unlock();
    window.addEventListener('pointerdown', unlock, { once: true });
    window.addEventListener('keydown', unlock, { once: true });

    this.applySettings(this.settings);
    void this.startup();
    requestAnimationFrame((t) => this.frame(t));
  }

  // --- Accounts & login gate --------------------------------------------------

  /** Boot: an account is required to play (offline is an explicit fallback). */
  private async startup(): Promise<void> {
    const user = await this.online.me();
    if (user) {
      await this.adoptAccount(user.handle); // may reload on account switch
      this.ui.setAccount(user.handle, user.role === 'admin');
      this.showMenu();
      return;
    }
    this.ui.showLoginGate(!this.online.online);
  }

  private get canPlay(): boolean {
    return this.online.user !== null || this.offlineMode;
  }

  /** Make this device's local save belong to `handle`. A different previous
   * owner means the cloud copy wins (or a clean slate for new accounts) so
   * two people on one device never share progress. Returns via reload when
   * the local save was replaced. */
  private async adoptAccount(handle: string): Promise<void> {
    const ACCOUNT_KEY = 'shape-shifters.account';
    const owner = localStorage.getItem(ACCOUNT_KEY);
    const save = await this.online.getSave();
    if (owner !== handle) {
      localStorage.setItem(ACCOUNT_KEY, handle);
      // Never let the previous owner's data leak into this account.
      localStorage.setItem('shape-shifters.profile.v1', '{}');
      localStorage.setItem('shape-shifters.progress.v1', '{}');
      localStorage.removeItem('shape-shifters.profile.v1.bak');
      localStorage.removeItem('shape-shifters.profile.v1.corrupt');
      mirrorAll();
      if (save.ok && save.value) {
        localStorage.setItem('shape-shifters.cloud-rev', String(save.value.revision));
        this.applySaveData(save.value.data); // reloads with the account's save
        return;
      }
      localStorage.setItem('shape-shifters.cloud-rev', '0');
      await this.pushCloudSave();
      location.reload();
      return;
    }
    // Same owner as last time: opportunistic push of anything unsynced.
    await this.pushCloudSave();
  }

  /** Fire-and-forget cloud push (no conflict UI here — the Online panel's
   * explicit sync handles the both-changed case). */
  private async pushCloudSave(): Promise<void> {
    if (!this.online.user) return;
    const base = Number(localStorage.getItem('shape-shifters.cloud-rev') ?? 0) || null;
    const put = await this.online.putSave(this.collectSaveData(), base);
    if (put.ok && 'revision' in put.value) {
      localStorage.setItem('shape-shifters.cloud-rev', String(put.value.revision));
    }
  }

  private async gateLogin(username: string, password: string): Promise<string | null> {
    const result = await this.online.login(username, password);
    if (!result.ok) {
      if (!this.online.online) this.ui.showLoginGate(true);
      return result.error;
    }
    await this.adoptAccount(result.value.handle);
    this.ui.hideLoginGate();
    this.ui.setAccount(result.value.handle, result.value.role === 'admin');
    this.showMenu();
    return null;
  }

  private async gateRegister(
    username: string,
    password: string,
    question: string,
    answer: string,
  ): Promise<string | null> {
    if (answer.trim().length === 0) return t('gate.err.answer-required');
    const result = await this.online.register(username, password, question, answer);
    if (!result.ok) {
      if (!this.online.online) this.ui.showLoginGate(true);
      return result.error;
    }
    await this.adoptAccount(result.value.handle);
    this.ui.hideLoginGate();
    this.ui.setAccount(result.value.handle, result.value.role === 'admin');
    this.showMenu();
    return null;
  }

  /** Lost-password windows: API errors mapped to the product's exact copy. */
  private async gateQuestion(username: string): Promise<{ question: string } | { error: string }> {
    const result = await this.online.securityQuestion(username);
    if (result.ok) return { question: result.value };
    if (result.status === 404 && result.error.includes('no security question')) {
      return { error: t('gate.err.no-question') };
    }
    if (result.status === 404) return { error: t('gate.err.no-user') };
    return { error: result.error };
  }

  private async gateVerify(username: string, answer: string): Promise<string | null> {
    const result = await this.online.verifyAnswer(username, answer);
    if (result.ok) return null;
    return result.status === 401 ? t('gate.err.wrong-answer') : result.error;
  }

  private async gateReset(
    username: string,
    answer: string,
    password: string,
  ): Promise<string | null> {
    const result = await this.online.resetWithAnswer(username, answer, password);
    if (result.ok) return null;
    return result.status === 401 ? t('gate.err.wrong-answer') : result.error;
  }

  private async logout(): Promise<void> {
    await this.pushCloudSave(); // last sync so nothing is stranded on-device
    await this.online.logout();
    this.offlineMode = false;
    this.ui.setAccount(null);
    this.ui.showLoginGate(!this.online.online);
  }

  // --- State transitions ------------------------------------------------------

  private showMenu(): void {
    this.state = 'menu';
    this.audio.stopMusic();
    this.ui.showMenu(
      LEVELS.map((level) => {
        const p = getProgress(level.meta.name);
        const track = TRACKS[level.song.id];
        const subtitle =
          p.attempts === 0
            ? t('menu.card.fresh', {
                track: track?.title ?? 'silent',
                diff: level.meta.difficulty ?? '?',
              })
            : t('menu.card.played', {
                best: Math.floor(p.best * 100),
                attempts: p.attempts,
                clears: p.completions,
              });
        return { name: level.meta.name, subtitle };
      }),
    );
  }

  /** Track of the active session (built-in level or playtest). */
  private activeTrack: Track | null = null;

  private currentTrack(): Track | null {
    return this.activeTrack;
  }

  private resolveTrack(songId: string): Track | null {
    const track = TRACKS[songId];
    if (!track) {
      // Missing-audio grace: the level still plays, silent, with a notice.
      console.warn(`No built-in track "${songId}"; playing silent.`);
      this.ui.flashNotice(t('audio.missing', { song: songId }));
      return null;
    }
    return track;
  }

  private startLevel(index: number): void {
    const level = LEVELS[index];
    if (!level) return;
    this.activeOnlineLevelId = null;
    this.startLevelData(level);
  }

  /** Start any validated level (built-in or downloaded community level). */
  private startLevelData(level: LevelData): void {
    this.audio.unlock();
    this.session = new Session(level);
    this.renderer.setLevel(this.session.level);
    this.state = 'playing';
    this.accumulator = 0;
    this.syncSnapshots(true);
    this.ui.showGame();
    this.ui.setPractice(false);
    this.ui.setMode(level.settings.mode);
    this.ui.flashAttempt(this.session.attempt);
    this.activeTrack = this.resolveTrack(level.song.id);
    if (this.activeTrack) this.audio.startMusic(this.activeTrack, 0);
  }

  private pause(): void {
    if (this.state !== 'playing' || !this.session) return;
    this.state = 'paused';
    this.audio.pauseMusic();
    this.ui.showPause(true);
  }

  private resume(): void {
    if (this.state !== 'paused' || !this.session) return;
    this.state = 'playing';
    this.ui.showPause(false);
    if (this.session.phase === 'playing') {
      this.audio.resumeMusic(this.session.simTimeS());
    }
  }

  private restart(): void {
    if (!this.session) return;
    this.session.respawn();
    this.state = 'playing';
    this.ui.showPause(false);
    this.ui.showGame();
    this.ui.flashAttempt(this.session.attempt);
    this.syncSnapshots(true);
    const track = this.currentTrack();
    if (track) this.audio.startMusic(track, this.session.respawnTimeS());
  }

  private exitToMenu(): void {
    if (this.playtesting) {
      this.returnToEditor();
      return;
    }
    this.session = null;
    this.activeOnlineLevelId = null;
    this.showMenu();
  }

  // --- Online -----------------------------------------------------------------

  private openAdmin(): void {
    if (this.online.user?.role !== 'admin') return; // server re-checks anyway
    const panel = this.ui.showAdminPanel(true);
    this.adminUi ??= new AdminUi(panel, this.online, () => void this.ui.showAdminPanel(false));
    void this.adminUi.show();
  }

  private openOnline(): void {
    const panel = this.ui.showOnlinePanel(true);
    this.onlineUi ??= new OnlineUi(panel, this.online, {
      onPlayOnlineLevel: (levelText, onlineId) => {
        try {
          const level = parseLevel(levelText); // never trust downloads blindly
          this.activeOnlineLevelId = onlineId;
          this.ui.showOnlinePanel(false);
          this.startLevelData(level);
          return true;
        } catch (e) {
          console.warn('Downloaded level failed validation.', e);
          return false;
        }
      },
      collectSave: () => this.collectSaveData(),
      applySave: (data) => this.applySaveData(data),
      onClose: () => void this.ui.showOnlinePanel(false),
    });
    void this.onlineUi.show();
  }

  private async publishLastCompletion(): Promise<void> {
    if (!this.lastCompletion) return;
    const { levelText, replay } = this.lastCompletion;
    const result = await this.online.publish(levelText, replay);
    this.ui.flashNotice(result.ok ? t('online.published', { id: result.value.id }) : result.error);
  }

  private async submitOnlineScore(session: Session): Promise<void> {
    if (this.activeOnlineLevelId === null || !this.online.user) return;
    const completion = session.completionReplay();
    if (!completion) return;
    const result = await this.online.submitScore(this.activeOnlineLevelId, completion.replay);
    if (result.ok && result.value.improved) {
      this.ui.flashNotice(t('online.score-sent', { percent: result.value.percent }));
    }
  }

  // --- Editor -----------------------------------------------------------------

  private openEditor(): void {
    this.state = 'editor';
    this.audio.stopMusic();
    this.ui.showEditorMode(true);
    this.editor = new Editor(this.editorHost, {
      onPlaytest: (level, useStartPos) => this.startPlaytest(level, useStartPos),
      onExit: () => this.closeEditor(),
    });
  }

  private closeEditor(): void {
    this.editor?.dispose();
    this.editor = null;
    this.ui.showEditorMode(false);
    this.showMenu();
  }

  private startPlaytest(level: LevelData, useStartPos: boolean): void {
    this.playtesting = true;
    this.audio.unlock();
    this.editorHost.style.display = 'none';
    this.ui.showEditorMode(false);
    this.session = new Session(level, { useStartPos, ephemeral: true });
    this.renderer.setLevel(this.session.level);
    this.state = 'playing';
    this.accumulator = 0;
    this.syncSnapshots(true);
    this.ui.showGame();
    this.ui.setPractice(false);
    this.ui.setMode(level.settings.mode);
    this.ui.setTesting(true);
    this.activeTrack = this.resolveTrack(level.song.id);
    if (this.activeTrack) this.audio.startMusic(this.activeTrack, 0);
  }

  private restartPlaytest(): void {
    if (!this.session) return;
    this.session.respawn();
    this.state = 'playing';
    this.ui.showGame();
    this.ui.setTesting(true);
    this.syncSnapshots(true);
  }

  private returnToEditor(): void {
    this.playtesting = false;
    this.session = null;
    this.audio.stopMusic();
    this.ui.setTesting(false);
    this.ui.hideAll();
    this.editorHost.style.display = '';
    this.ui.showEditorMode(true);
    this.state = 'editor';
  }

  private togglePractice(): void {
    if (!this.session) return;
    const next = !this.session.practiceMode;
    this.session.setPractice(next);
    this.ui.setPractice(next);
  }

  // --- Save backup -------------------------------------------------------------

  /** Export every persistent store as one JSON file the player can keep. */
  /** Whole-save payload (same shape as file export; also the cloud blob). */
  private collectSaveData(): string {
    const keys = [
      'shape-shifters.profile.v1',
      'shape-shifters.progress.v1',
      'shape-shifters.settings.v1',
    ];
    const data: Record<string, unknown> = {
      exportVersion: 1,
      exportedAt: new Date().toISOString(),
    };
    for (const k of keys) {
      const raw = localStorage.getItem(k);
      if (raw) data[k] = JSON.parse(raw);
    }
    return JSON.stringify(data);
  }

  /** Replace the local save with an export payload; reload re-hydrates. */
  private applySaveData(text: string): boolean {
    try {
      const data = JSON.parse(text) as Record<string, unknown>;
      if (data.exportVersion !== 1) throw new Error('Unknown save export version');
      for (const k of [
        'shape-shifters.profile.v1',
        'shape-shifters.progress.v1',
        'shape-shifters.settings.v1',
      ]) {
        if (data[k] !== undefined) localStorage.setItem(k, JSON.stringify(data[k]));
      }
      location.reload();
      return true;
    } catch (e) {
      console.warn('Save apply failed.', e);
      return false;
    }
  }

  private exportSave(): void {
    const blob = new Blob([JSON.stringify(JSON.parse(this.collectSaveData()), null, 2)], {
      type: 'application/json',
    });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `shape-shifters-save-${new Date().toISOString().slice(0, 10)}.json`;
    a.click();
    URL.revokeObjectURL(a.href);
  }

  private importSave(): void {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.json';
    input.addEventListener('change', () => {
      const file = input.files?.[0];
      if (!file) return;
      void file.text().then((text) => {
        if (!confirm('Replace your current save with this file? This cannot be undone.')) return;
        if (!this.applySaveData(text)) alert('Could not import save: invalid file.');
      });
    });
    input.click();
  }

  /** Resolve equipped cosmetic ids to render values (hex colors / styles). */
  private resolveCosmetics(): {
    primary: string;
    secondary: string;
    core: string;
    trail: string;
    burst: string;
    shell: string;
  } {
    const find = (id: string, fallback: string): string =>
      COSMETICS.find((c) => c.id === id)?.value ?? fallback;
    const e = this.profile.equipped;
    return {
      primary: find(e.primary, '#22d3ee'),
      secondary: find(e.secondary, '#f0abfc'),
      core: find(e.core, 'diamond'),
      trail: find(e.trail, 'streak'),
      burst: find(e.burst, 'ring'),
      shell: find(e.shell, 'solid'),
    };
  }

  private applySettings(s: Settings): void {
    this.settings = s;
    saveSettings(s);
    this.audio.applySettings({
      musicVolume: s.musicVolume,
      sfxVolume: s.sfxVolume,
      offsetS: s.audioOffsetMs / 1000,
    });
    this.renderer.setQuality({
      glow: s.glow,
      particles: s.particles,
      reducedFlashing: s.reducedFlashing,
      reducedMotion: s.reducedMotion,
      lowDetail: s.lowDetail,
      colorblind: s.colorblind,
    });
    this.input.setBindings({
      keys: s.keyJump,
      leftKeys: s.keyLeft,
      rightKeys: s.keyRight,
      gamepadButtons: s.padJump,
    });
    if (this.renderer.renderScale !== s.renderScale) {
      this.renderer.renderScale = s.renderScale;
      this.renderer.resize();
    }
    // UI scale + high contrast are plain CSS knobs on the root element.
    document.documentElement.style.setProperty('--ui-scale', String(s.uiScale));
    document.documentElement.classList.toggle('high-contrast', s.highContrast);
  }

  // --- Main loop ----------------------------------------------------------------

  private frame(nowMs: number): void {
    requestAnimationFrame((t) => this.frame(t));
    this.input.pollShellButtons();
    // FPS cap: skip whole frames; dt accounting below stays correct because
    // lastFrameMs only advances on processed frames.
    if (this.settings.fpsCap > 0 && nowMs - this.lastFrameMs < 1000 / this.settings.fpsCap - 1) {
      return;
    }
    const dtMs = this.lastFrameMs === 0 ? 16.7 : nowMs - this.lastFrameMs;
    this.lastFrameMs = nowMs;

    const session = this.session;
    if (this.state === 'playing' && session) {
      if (session.phase === 'playing') {
        // Timewarp triggers scale how fast sim time flows relative to wall time.
        this.accumulator += Math.min(dtMs / 1000, 0.25) * session.sim.state.trig.timewarp;
        let ticks = 0;
        while (this.accumulator >= TICK_DT && ticks < MAX_TICKS_PER_FRAME) {
          this.prevSnapFromCurr();
          session.step(this.input.sampleWord());
          this.currFromSim();
          this.accumulator -= TICK_DT;
          ticks++;
          if (session.phase !== 'playing') break;
        }
        this.audio.updateMusic(session.simTimeS());
        // step() may have changed the phase; re-read through a widened alias.
        const phase = session.phase as SessionPhase;
        if (phase === 'dead') {
          this.audio.pauseMusic();
          this.audio.sfxDeath();
          if (!session.ephemeral && !session.practiceMode) {
            recordDeath(this.profile, session.sim.state.jumps, session.simTimeS());
          }
        } else if (phase === 'complete') {
          this.audio.pauseMusic();
          this.audio.sfxComplete();
          const p = getProgress(session.levelId);
          const award = session.ephemeral
            ? null
            : awardCompletion(this.profile, session.level.data, {
                jumps: session.sim.state.jumps,
                timeS: session.simTimeS(),
                practice: session.practiceMode,
                firstClear: session.firstClear,
                collectedIds: session.sim.state.collected,
              });
          // Full-run replay: enables publish (playtests) + online scores.
          const completion = session.completionReplay();
          this.lastCompletion = completion
            ? { levelText: completion.levelText, replay: completion.replay }
            : null;
          this.ui.showComplete(
            session.levelId,
            session.attempt,
            session.sim.state.jumps,
            session.simTimeS(),
            p,
            award,
            this.playtesting && completion !== null && this.online.user !== null,
          );
          void this.submitOnlineScore(session);
          // Progress follows the account: sync after every real completion.
          if (!session.ephemeral) void this.pushCloudSave();
        }
      } else if (session.phase === 'dead') {
        session.deadForS += dtMs / 1000;
        if (session.deadForS >= RESPAWN_DELAY_S) {
          const resumeAtS = session.respawnTimeS();
          session.respawn();
          this.syncSnapshots(true);
          if (!session.practiceMode) this.ui.flashAttempt(session.attempt);
          const track = this.currentTrack();
          if (track) this.audio.startMusic(track, resumeAtS);
        }
      }

      const alpha = Math.min(1, this.accumulator / TICK_DT);
      const snap = this.buildSnapshot(alpha);
      this.renderer.draw(snap);
      this.postfx.render(
        snap.shaderFx,
        this.renderer.playerScreenX,
        this.renderer.playerScreenY,
        snap.timeS,
      );
      if (session.isPlatformer) {
        this.ui.updatePlatformerStats(session.simTimeS(), session.deaths);
      } else {
        this.ui.updateHud(session.sim.progress(), session.best);
      }
    } else if (this.state === 'paused' && session) {
      const snap = this.buildSnapshot(1);
      this.renderer.draw(snap);
      this.postfx.render(
        snap.shaderFx,
        this.renderer.playerScreenX,
        this.renderer.playerScreenY,
        snap.timeS,
      );
    }
  }

  private prevSnapFromCurr(): void {
    this.prevSnap.x = this.currSnap.x;
    this.prevSnap.y = this.currSnap.y;
    this.prevSnap.rot = this.currSnap.rot;
    this.prevSnap.dualY = this.currSnap.dualY;
    this.prevSnap.dualRot = this.currSnap.dualRot;
  }

  private currFromSim(): void {
    const s = (this.session as Session).sim.state;
    this.currSnap.x = s.x;
    this.currSnap.y = s.y;
    this.currSnap.rot = s.rotation;
    if (s.dual) {
      this.currSnap.dualY = s.dual.y;
      this.currSnap.dualRot = s.dual.rotation;
    }
  }

  private syncSnapshots(reset: boolean): void {
    if (!this.session) return;
    this.currFromSim();
    if (reset) this.prevSnapFromCurr();
  }

  private buildSnapshot(alpha: number): RenderSnapshot {
    const session = this.session as Session;
    const p = this.prevSnap;
    const c = this.currSnap;
    const sim = session.sim.state;
    return {
      playerX: p.x + (c.x - p.x) * alpha,
      playerY: p.y + (c.y - p.y) * alpha,
      playerRotation: p.rot + (c.rot - p.rot) * alpha,
      form: sim.form,
      gravityDir: sim.gravityDir,
      mini: sim.mini,
      mirror: sim.mirror,
      mode: session.level.mode,
      facing: sim.facing,
      dashing: sim.dashing,
      dual: sim.dual
        ? {
            y: p.dualY + (c.dualY - p.dualY) * alpha,
            rotation: p.dualRot + (c.dualRot - p.dualRot) * alpha,
            gravityDir: sim.dual.gravityDir,
          }
        : null,
      status: sim.status,
      practice: session.practiceMode,
      checkpoints: session.checkpointMarkers(),
      groups: sim.trig.groups,
      channels: sim.trig.channels,
      cameraZoom: sim.trig.camera.zoom,
      cameraDx: sim.trig.camera.dx,
      cameraDy: sim.trig.camera.dy,
      cameraRot: sim.trig.camera.rot,
      cameraStatic: sim.trig.camera.staticOn
        ? { x: sim.trig.camera.staticX, y: sim.trig.camera.staticY }
        : null,
      shake: session.sim.currentShake(),
      shaderFx: sim.trig.shaderFx,
      areas: sim.trig.areas,
      enter: sim.trig.enter,
      gradient: sim.trig.gradient,
      cameraEdges: sim.trig.edges,
      taken: sim.usedZones,
      items: sim.trig.items,
      cosmetics: this.resolveCosmetics(),
      timeS: session.simTimeS(),
      progress: session.sim.progress(),
      attempt: session.attempt,
    };
  }
}

// Restore any evicted/corrupt localStorage keys from the IndexedDB mirror
// before the app reads them (constructor field initializers are synchronous).
void restoreFromMirror().finally(() => new App());
